import { forwardRef } from 'react'

/**
 * A container component with optional padding and maximum width styling.
 *
 * @component
 * @example
 * return (
 *   <Container
 *     outerClassName="my-outer-class"
 *     className="my-inner-class"
 *     padding
 *     maxWidth={false}
 *     style={{ backgroundColor: 'lightgray' }}
 *   >
 *     <p>Content goes here</p>
 *   </Container>
 * )
 * @param {object} props - The props for the component.
 * @param {string} [props.outerClassName=''] - Additional classes for the outer section.
 * @param {string} [props.className=''] - Additional classes for the inner div.
 * @param {React.ReactNode} props.children - The content to be rendered inside the container.
 * @param {boolean} [props.padding=true] - Whether to apply padding to the outer section.
 * @param {boolean} [props.maxWidth=true] - Whether to apply maximum width to the inner div.
 * @param {React.CSSProperties} [props.style={}] - Inline styles for the outer section.
 * @param {object} [props.rest] - Additional props for the inner div.
 * @param {React.Ref} ref - Ref to be forwarded to the inner div.
 * @returns {JSX.Element} The container component with optional styling and content.
 */
const Container = forwardRef(
  ({
    outerClassName = '',
    className = '',
    children,
    padding = true,
    maxWidth = true,
    style = {},
    ...args
  }, ref) => {
    return (
      <section
        className={`relative w-full ${
          padding ? 'px-smallMobile md:px-mediumDesktop' : ''
        } ${outerClassName}`}
        style={style}
      >
        <div
          ref={ref}
          className={`relative w-full ${
            maxWidth ? 'max-w-1440' : ''
          } mx-auto ${className}`}
          {...args}
        >
          {children}
        </div>
      </section>
    )
  },
)

export default Container
